/**
 * Spotify Song Matching Service
 *
 * Matches parsed song input to Spotify catalog entries
 * using fuzzy matching and scoring algorithms.
 *
 * Mirrors the structure of songMatcher.js but uses Spotify API.
 */

import { searchTracks } from './spotifyService.js';
import { buildSearchQuery } from '../utils/textParser.js';

/**
 * @typedef {Object} SpotifyMatchResult
 * @property {string} title - Original parsed title
 * @property {string} artist - Original parsed artist
 * @property {string} originalLine - Original input line
 * @property {number} lineNumber - Line number in input
 * @property {'matched'|'uncertain'|'not_found'} status - Match status
 * @property {Object|null} match - The matched song or null
 * @property {number} matchScore - Score from 0-1
 * @property {Array} alternatives - Alternative matches
 */

/**
 * Match a single parsed song to the Spotify catalog
 *
 * @param {Object} parsedSong - Parsed song object from textParser
 * @returns {Promise<SpotifyMatchResult>} The match result
 */
export async function matchSongSpotify(parsedSong) {
  const query = buildSearchQuery(parsedSong);

  if (!query || query.trim().length < 2) {
    return {
      ...parsedSong,
      status: 'not_found',
      match: null,
      matchScore: 0,
      alternatives: [],
    };
  }

  const results = await searchTracks(query, 10);

  if (results.length === 0) {
    return {
      ...parsedSong,
      status: 'not_found',
      match: null,
      matchScore: 0,
      alternatives: [],
    };
  }

  // Score each result
  const scoredResults = results.map(track => ({
    track,
    score: calculateMatchScore(parsedSong, track),
  }));

  // Sort by score descending
  scoredResults.sort((a, b) => b.score - a.score);

  const bestMatch = scoredResults[0];

  // Thresholds for match confidence (same as Apple Music)
  const MATCH_THRESHOLD = 0.6;
  const UNCERTAIN_THRESHOLD = 0.4;

  // Format the match object
  const formatMatch = (result) => ({
    id: result.track.id,
    title: result.track.name || 'Unknown',
    artist: result.track.artists?.map(a => a.name).join(', ') || 'Unknown',
    album: result.track.album?.name || '',
    artwork: result.track.album?.images?.[0] || null,
    duration: result.track.duration_ms || 0,
    score: result.score,
  });

  if (bestMatch.score >= MATCH_THRESHOLD) {
    return {
      ...parsedSong,
      status: 'matched',
      match: formatMatch(bestMatch),
      matchScore: bestMatch.score,
      alternatives: scoredResults.slice(1, 4).map(formatMatch),
    };
  }

  if (bestMatch.score >= UNCERTAIN_THRESHOLD) {
    return {
      ...parsedSong,
      status: 'uncertain',
      match: formatMatch(bestMatch),
      matchScore: bestMatch.score,
      alternatives: scoredResults.slice(1, 4).map(formatMatch),
    };
  }

  return {
    ...parsedSong,
    status: 'not_found',
    match: null,
    matchScore: bestMatch.score,
    alternatives: scoredResults.slice(0, 4).map(formatMatch),
  };
}

/**
 * Calculate match score between parsed input and Spotify track result
 *
 * Uses a weighted combination of:
 * - Title similarity (60%)
 * - Artist similarity (40%)
 *
 * @param {Object} input - Parsed song input
 * @param {Object} track - Spotify track result
 * @returns {number} Score between 0 and 1
 */
function calculateMatchScore(input, track) {
  const resultTitle = normalize(track.name || '');
  const resultArtist = normalize(track.artists?.map(a => a.name).join(' ') || '');
  const inputTitle = normalize(input.title || '');
  const inputArtist = normalize(input.artist || '');

  // Title similarity (60% weight)
  const titleScore = calculateSimilarity(inputTitle, resultTitle);

  // Artist similarity (40% weight)
  let artistScore = 1.0; // Default to perfect if no artist provided
  if (inputArtist) {
    artistScore = calculateSimilarity(inputArtist, resultArtist);
  }

  // Weight the scores
  const finalScore = (titleScore * 0.6) + (artistScore * 0.4);

  return finalScore;
}

/**
 * Normalize a string for comparison
 *
 * - Lowercase
 * - Remove diacritics (é → e)
 * - Remove punctuation
 * - Collapse whitespace
 *
 * @param {string} str - Input string
 * @returns {string} Normalized string
 */
function normalize(str) {
  if (!str) return '';

  return str
    .toLowerCase()
    // Remove diacritics/accents
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove common suffixes that affect matching
    .replace(/\s*\(remaster(ed)?\)/gi, '')
    .replace(/\s*\(deluxe( edition)?\)/gi, '')
    .replace(/\s*-\s*single$/gi, '')
    // Remove punctuation except spaces
    .replace(/[^\w\s]/g, '')
    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Calculate similarity between two strings
 *
 * Uses a combination of:
 * - Token overlap (Dice coefficient)
 * - Levenshtein distance
 * - Substring containment
 *
 * @param {string} a - First string (normalized)
 * @param {string} b - Second string (normalized)
 * @returns {number} Similarity score 0-1
 */
function calculateSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;

  // Token overlap (Dice coefficient)
  const tokensA = new Set(a.split(' ').filter(t => t.length > 0));
  const tokensB = new Set(b.split(' ').filter(t => t.length > 0));

  if (tokensA.size === 0 || tokensB.size === 0) {
    return 0;
  }

  const intersection = [...tokensA].filter(t => tokensB.has(t));
  const diceScore = (2 * intersection.length) / (tokensA.size + tokensB.size);

  // Levenshtein-based score
  const maxLen = Math.max(a.length, b.length);
  const distance = levenshteinDistance(a, b);
  const levenScore = maxLen > 0 ? 1 - (distance / maxLen) : 0;

  // Containment check (bonus if one contains the other)
  let containmentBonus = 0;
  if (a.includes(b) || b.includes(a)) {
    containmentBonus = 0.2;
  }

  // Weighted combination
  const baseScore = (diceScore * 0.5) + (levenScore * 0.5);

  return Math.min(1, baseScore + containmentBonus);
}

/**
 * Calculate Levenshtein (edit) distance between two strings
 *
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} Edit distance
 */
function levenshteinDistance(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Use two rows instead of full matrix for memory efficiency
  let prevRow = Array(b.length + 1).fill(0).map((_, i) => i);
  let currRow = Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    currRow[0] = i;

    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      currRow[j] = Math.min(
        prevRow[j] + 1,       // deletion
        currRow[j - 1] + 1,   // insertion
        prevRow[j - 1] + cost // substitution
      );
    }

    [prevRow, currRow] = [currRow, prevRow];
  }

  return prevRow[b.length];
}

/**
 * Process multiple songs with rate limiting and progress reporting
 *
 * @param {Array} parsedSongs - Array of parsed songs
 * @param {Object} options - Processing options
 * @param {number} options.concurrency - Number of concurrent searches (default: 3)
 * @param {number} options.delayMs - Delay between batches (default: 100)
 * @param {function} options.onProgress - Progress callback
 * @returns {Promise<SpotifyMatchResult[]>} Array of match results
 */
export async function matchAllSongsSpotify(parsedSongs, options = {}) {
  const {
    concurrency = 3,
    delayMs = 100,
    onProgress,
  } = options;

  const results = [];
  const total = parsedSongs.length;

  // Process in batches for rate limiting
  for (let i = 0; i < parsedSongs.length; i += concurrency) {
    const batch = parsedSongs.slice(i, i + concurrency);

    // Process batch concurrently
    const batchResults = await Promise.all(
      batch.map(song => matchSongSpotify(song).catch(error => ({
        ...song,
        status: 'not_found',
        match: null,
        matchScore: 0,
        alternatives: [],
        error: error.message,
      })))
    );

    results.push(...batchResults);

    // Report progress
    if (onProgress) {
      onProgress({
        completed: results.length,
        total,
        current: batchResults,
        matched: results.filter(r => r.status === 'matched').length,
        uncertain: results.filter(r => r.status === 'uncertain').length,
        notFound: results.filter(r => r.status === 'not_found').length,
      });
    }

    // Add delay between batches to respect rate limits
    if (i + concurrency < parsedSongs.length && delayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return results;
}

/**
 * Get summary statistics for match results
 *
 * @param {SpotifyMatchResult[]} results - Array of match results
 * @returns {Object} Summary statistics
 */
export function getMatchSummarySpotify(results) {
  const matched = results.filter(r => r.status === 'matched');
  const uncertain = results.filter(r => r.status === 'uncertain');
  const notFound = results.filter(r => r.status === 'not_found');

  return {
    total: results.length,
    matched: matched.length,
    uncertain: uncertain.length,
    notFound: notFound.length,
    successRate: results.length > 0
      ? ((matched.length + uncertain.length) / results.length * 100).toFixed(1)
      : 0,
    avgMatchScore: results.length > 0
      ? (results.reduce((sum, r) => sum + r.matchScore, 0) / results.length).toFixed(2)
      : 0,
  };
}

/**
 * Filter results to get only songs ready for import
 *
 * @param {SpotifyMatchResult[]} results - All match results
 * @param {Object} options - Filter options
 * @param {boolean} options.includeUncertain - Include uncertain matches
 * @returns {SpotifyMatchResult[]} Filtered results ready for import
 */
export function getImportableResultsSpotify(results, options = {}) {
  const { includeUncertain = false } = options;

  return results.filter(r => {
    if (r.status === 'matched') return true;
    if (r.status === 'uncertain' && includeUncertain) return true;
    return false;
  });
}
